import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, SearchCheck, Wand2, Sparkles, HeartHandshake, Check, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SERVICE_SLUGS, SERVICE_IMAGES, HOME_TRANSFORMATIONS, waLink } from "../config";
import { Reveal, MaskedLine, Page, Eyebrow, EASE, Tilt3D, Petals } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SEO from "../components/SEO";
import { WhatsAppIcon } from "../components/icons";

const STEP_ICONS = [SearchCheck, Wand2, Sparkles, HeartHandshake];

const Home = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 600], [0, 70]);

  const specialtyCards = [
    { slug: "mechas-balayage", img: SERVICE_IMAGES["mechas-balayage"] },
    { slug: "coloracao", img: SERVICE_IMAGES["coloracao"] },
    { slug: "cortes-finalizacao", img: SERVICE_IMAGES["cortes-finalizacao"] },
    { slug: "tratamentos-capilares", img: SERVICE_IMAGES["tratamentos-capilares"] },
    { slug: "botox-alinhamento", img: SERVICE_IMAGES["botox-alinhamento"] },
    { slug: "progressiva-alisamentos", img: SERVICE_IMAGES["progressiva-alisamentos"] },
  ];

  const quotes = t.home.quotes;
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    setQuoteIdx(0);
  }, [quotes]);
  useEffect(() => {
    const id = setInterval(() => setQuoteIdx((i) => (i + 1) % quotes.length), 5000);
    return () => clearInterval(id);
  }, [quotes.length]);
  const nextQuote = () => setQuoteIdx((i) => (i + 1) % quotes.length);
  const prevQuote = () => setQuoteIdx((i) => (i - 1 + quotes.length) % quotes.length);

  return (
    <Page>
      <SEO title={t.meta.home.title} desc={t.meta.home.desc} />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-cream pt-28 sm:pt-36" data-testid="hero">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-20 h-[30rem] w-[30rem] rounded-full bg-blush/60 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute right-0 top-0 h-[26rem] w-[26rem] rounded-full bg-rose/15 blur-3xl" />
        <Petals />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:pb-24">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
              className="eyebrow mb-7 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block h-px w-8 bg-gradient-to-r from-rose to-gold" />
              {t.home.heroEyebrow}
            </motion.p>

            <h1 className="font-serif text-[2.75rem] font-semibold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-[4.4rem]" data-testid="hero-title">
              {t.home.heroLines.map((line, i) => (
                <MaskedLine key={i} delay={0.25 + i * 0.14}>
                  {i === 1 ? <em className="font-medium italic text-rose-gradient">{line}</em> : line}
                </MaskedLine>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-ink/65 sm:text-lg"
              data-testid="hero-text"
            >
              {t.home.heroText}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href={waLink(t.waEval)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2.5 rounded-full bg-rosedeep px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_18px_40px_-14px_rgba(212,122,142,0.85)] transition-all duration-300 hover:bg-ink hover:shadow-[0_22px_45px_-14px_rgba(43,27,32,0.6)]"
              >
                {t.home.ctaPrimary}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/servicos"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2.5 rounded-full border border-rose/50 px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-ink transition-all duration-300 hover:border-rose hover:bg-blush/50"
              >
                {t.home.ctaSecondary}
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
            className="relative mx-auto w-full max-w-md lg:max-w-none"
          >
            <div aria-hidden="true" className="absolute -inset-3 arch-frame border border-rose/40" />
            <Tilt3D max={6} className="relative">
              <div className="arch-frame img-zoom relative overflow-hidden shadow-[0_45px_90px_-30px_rgba(168,94,112,0.55)]">
                <motion.img
                  src="/assets/salon-interior.jpg"
                  alt="Interior do salão Bloom Beauty Concept Hair"
                  className="h-[30rem] w-full object-cover sm:h-[34rem]"
                  style={{ y: parallaxY, scale: 1.12 }}
                  data-testid="hero-image"
                />
              </div>
            </Tilt3D>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
              className="animate-float-soft absolute -bottom-6 -left-4 sm:-left-10 flex items-center gap-3 rounded-2xl border border-rose/30 bg-white/85 px-5 py-4 shadow-[0_20px_45px_-18px_rgba(168,94,112,0.5)] backdrop-blur-md"
            >
              <img src="/assets/monique-gray.jpg" alt="Monique Santos" className="h-12 w-12 rounded-full object-cover ring-2 ring-rose/30" />
              <div>
                <p className="font-serif text-lg font-semibold leading-none text-ink">Monique Santos</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-rose">Hairstylist</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <Marquee items={t.home.marquee} />
      </section>

      {/* ============ WELCOME ============ */}
      <section className="relative overflow-hidden bg-white py-24 sm:py-32" data-testid="welcome-section">
        <div aria-hidden="true" className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-blush/40 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>{t.home.welcome.eyebrow}</Eyebrow>
            <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
              {t.home.welcome.title}
            </h2>
            <p className="mt-9 max-w-xl text-base leading-relaxed text-ink/65 sm:text-lg">{t.home.welcome.p}</p>
            <p className="mt-7 font-script text-3xl text-rose-gradient sm:text-4xl">{t.home.welcome.tag}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Tilt3D max={4}>
              <div className="img-zoom relative overflow-hidden rounded-[2rem] shadow-[0_40px_75px_-30px_rgba(168,94,112,0.5)] ring-1 ring-blush">
                <img src="/assets/salon-event.jpg" alt="Detalhe do espaço Bloom" className="h-[26rem] w-full object-cover sm:h-[30rem]" loading="lazy" />
              </div>
            </Tilt3D>
          </Reveal>
        </div>
      </section>

      {/* ============ SPECIALTIES ============ */}
      <section className="bg-gradient-to-b from-cream via-blush/25 to-cream py-24 sm:py-32" data-testid="specialties-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <Eyebrow>{t.home.specialties.eyebrow}</Eyebrow>
            <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
              {t.home.specialties.title}
            </h2>
            <p className="mt-9 text-base text-ink/60 sm:text-lg">{t.home.specialties.sub}</p>
          </Reveal>

          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {specialtyCards.map((card, i) => (
              <Reveal key={card.slug} delay={(i % 3) * 0.12}>
                <Tilt3D max={4}>
                  <Link
                    to={`/servicos/${card.slug}`}
                    data-testid={`specialty-${card.slug}`}
                    className="card-lift group block overflow-hidden rounded-3xl bg-white ring-1 ring-blush transition-shadow hover:ring-rose/50"
                  >
                    <div className="img-zoom relative h-64 overflow-hidden bg-blush/30 sm:h-72">
                      <img src={card.img} alt={t.services[card.slug].name} className="h-full w-full object-contain" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-rosedeep/55 via-rose/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-serif text-2xl font-semibold text-ink transition-colors group-hover:text-rosedeep">
                        {t.services[card.slug].name}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-ink/55">{t.services[card.slug].short}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gold">
                        {t.common.learnMore}
                        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Tilt3D>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MONIQUE ============ */}
      <section className="relative overflow-hidden bg-white py-24 sm:py-32" data-testid="monique-section">
        <div aria-hidden="true" className="pointer-events-none absolute -left-32 top-1/3 h-80 w-80 rounded-full bg-blush/45 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal className="relative mx-auto w-full max-w-md">
            <div aria-hidden="true" className="absolute -inset-3 arch-frame border border-rose/40" />
            <Tilt3D max={5}>
              <div className="arch-frame img-zoom overflow-hidden shadow-[0_40px_75px_-28px_rgba(168,94,112,0.55)]">
                <img
                  src="/assets/monique-gray.jpg"
                  alt="Monique Santos, hairstylist da Bloom Beauty Concept Hair"
                  className="h-[30rem] w-full object-cover sm:h-[34rem]"
                  loading="lazy"
                  data-testid="monique-photo-gray"
                />
              </div>
            </Tilt3D>
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow>{t.home.monique.eyebrow}</Eyebrow>
            <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
              {t.home.monique.title}
            </h2>
            <p className="mt-9 text-base leading-relaxed text-ink/65 sm:text-lg">{t.home.monique.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-ink/65">{t.home.monique.p2}</p>
            <Link
              to="/sobre"
              data-testid="monique-cta"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full border border-rosedeep px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.16em] text-rosedeep transition-all duration-300 hover:bg-rosedeep hover:text-white hover:shadow-[0_18px_40px_-14px_rgba(212,122,142,0.8)]"
            >
              {t.home.monique.cta}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ TRANSFORMATIONS ============ */}
      <section className="bg-cream py-24 sm:py-32" data-testid="transformations-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-8">
            <Reveal>
              <Eyebrow>{t.home.transformations.eyebrow}</Eyebrow>
              <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
                {t.home.transformations.title}
              </h2>
              <p className="mt-9 max-w-xl text-base text-ink/60 sm:text-lg">{t.home.transformations.p}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/transformacoes"
                data-testid="transformations-cta"
                className="group inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.2em] text-rosedeep"
              >
                {t.common.seeTransformations}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOME_TRANSFORMATIONS.map((item, i) => (
              <Reveal key={item.key} delay={i * 0.1} className={i % 2 === 1 ? "lg:mt-12" : ""}>
                <div className="img-zoom card-lift overflow-hidden rounded-3xl bg-blush/30 ring-1 ring-blush transition-shadow hover:ring-rose/50">
                  <img
                    src={item.img}
                    alt={t.transformationsPage.captions[item.key]}
                    className="h-80 w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EXPERIENCE ============ */}
      <section className="bg-white py-24 sm:py-32" data-testid="experience-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-16 text-center">
            <Eyebrow className="justify-center">{t.home.experience.eyebrow}</Eyebrow>
            <h2 className="mx-auto mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl lg:text-5xl">
              {t.home.experience.title}
            </h2>
          </Reveal>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {t.home.experience.steps.map((step, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={step.n} delay={i * 0.12} className="group relative">
                  <span className="font-serif text-6xl font-semibold text-blush transition-colors duration-500 group-hover:text-rose/60">
                    {step.n}
                  </span>
                  <div className="mt-4 flex h-12 w-12 items-center justify-center rounded-full border border-rose/40 bg-blush/30 text-rose shadow-[0_10px_25px_-12px_rgba(212,122,142,0.6)] transition-all duration-500 group-hover:bg-rose group-hover:text-white group-hover:shadow-[0_16px_32px_-12px_rgba(212,122,142,0.8)]">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-ink">{step.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/55">{step.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ EVALUATION ============ */}
      <section className="relative overflow-hidden bg-rosedeep py-24 sm:py-32" data-testid="evaluation-section">
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <Petals />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow !text-blush">{t.home.evaluation.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
              {t.home.evaluation.title}
            </h2>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">{t.home.evaluation.p}</p>
            <a
              href={waLink(t.waEval)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="evaluation-cta"
              className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-rosedeep shadow-[0_20px_45px_-16px_rgba(43,27,32,0.5)] transition-all duration-300 hover:bg-ink hover:text-white"
            >
              {t.home.evaluation.cta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="grid gap-3 sm:grid-cols-2">
              {t.home.evaluation.items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 text-sm font-medium text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/35 hover:bg-white/15"
                >
                  <Check className="w-4 h-4 shrink-0 text-blush" strokeWidth={2.5} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ============ SALON ============ */}
      <section className="bg-cream py-24 sm:py-32" data-testid="salon-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div className="grid grid-cols-2 gap-5">
            <Reveal className="col-span-2">
              <div className="img-zoom overflow-hidden rounded-3xl shadow-[0_30px_60px_-25px_rgba(168,94,112,0.45)] ring-1 ring-blush">
                <img src="/assets/salon-facade.jpg" alt="Fachada do salão Bloom" className="h-64 w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="img-zoom overflow-hidden rounded-3xl shadow-[0_25px_50px_-22px_rgba(168,94,112,0.4)] ring-1 ring-blush">
                <img src="/assets/salon-interior.jpg" alt="Interior do salão Bloom" className="h-52 w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="img-zoom overflow-hidden rounded-3xl shadow-[0_25px_50px_-22px_rgba(168,94,112,0.4)] ring-1 ring-blush">
                <img src="/assets/salon-event.jpg" alt="Detalhes do salão Bloom" className="h-52 w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Eyebrow>{t.home.salon.eyebrow}</Eyebrow>
            <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
              {t.home.salon.title}
            </h2>
            <p className="mt-9 max-w-xl text-base leading-relaxed text-ink/65 sm:text-lg">{t.home.salon.p}</p>
            <Link
              to="/galeria"
              data-testid="salon-cta"
              className="group mt-8 inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.2em] text-rosedeep"
            >
              {t.common.seeGallery}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ QUOTE ============ */}
      <section className="relative overflow-hidden bg-blush/50 py-24 sm:py-32" data-testid="quote-section">
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40 blur-3xl" />
        <Petals />
        <Reveal className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <span aria-hidden="true" className="font-serif text-8xl leading-none text-rose/50">“</span>
          <p className="-mt-6 font-serif text-3xl font-medium italic leading-snug text-ink sm:text-4xl lg:text-[2.75rem]" data-testid="quote-text">
            {t.home.quote}
          </p>
          <div className="mx-auto mt-10 h-0.5 w-24 rounded-full bg-gradient-to-r from-rose to-gold" />
          <p className="mt-5 font-script text-3xl text-rose-gradient">Bloom Beauty Concept Hair</p>
        </Reveal>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative overflow-hidden bg-white py-24 sm:py-32" data-testid="final-cta-section">
        <div aria-hidden="true" className="pointer-events-none absolute -right-28 bottom-0 h-72 w-72 rounded-full bg-blush/50 blur-3xl" />
        <Reveal className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <h2 className="font-serif text-3xl font-semibold leading-tight text-ink sm:text-5xl">
            {t.home.final.title}
          </h2>
          <p className="mt-6 text-base text-ink/60 sm:text-lg">{t.home.final.p}</p>
          <a
            href={waLink(t.waEval)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-btn"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-rosedeep px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_18px_40px_-14px_rgba(212,122,142,0.85)] transition-all duration-300 hover:bg-ink"
          >
            <WhatsAppIcon className="w-5 h-5" />
            {t.home.final.cta}
          </a>
        </Reveal>
      </section>

      {/* ============ MOTIVATIONAL QUOTES CAROUSEL ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-blush/40 to-cream py-20 sm:py-24" data-testid="quotes-section">
        <Petals />
        <Reveal className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <div
            className="relative rounded-[2.5rem] border border-gold/25 bg-white/80 px-8 py-12 text-center shadow-[0_30px_70px_-30px_rgba(168,94,112,0.4)] backdrop-blur-sm sm:px-14"
            data-testid="quotes-carousel"
          >
            <Quote className="mx-auto h-8 w-8 text-rose/50" strokeWidth={1.5} />
            <div className="relative mt-6 flex min-h-[7.5rem] items-center justify-center sm:min-h-[6.5rem]">
              <AnimatePresence mode="wait">
                <motion.p
                  key={quoteIdx}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.55, ease: EASE }}
                  className="font-serif text-2xl font-medium italic leading-snug text-ink sm:text-3xl"
                  data-testid="quote-carousel-text"
                >
                  “{quotes[quoteIdx]}”
                </motion.p>
              </AnimatePresence>
            </div>
            <div className="mt-8 flex items-center justify-center gap-5">
              <button
                onClick={prevQuote}
                aria-label="Frase anterior"
                data-testid="quote-prev"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-rose/40 text-rosedeep transition-all duration-300 hover:bg-rosedeep hover:text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                {quotes.map((_, d) => (
                  <button
                    key={d}
                    onClick={() => setQuoteIdx(d)}
                    aria-label={`Frase ${d + 1}`}
                    data-testid={`quote-dot-${d}`}
                    className={`h-2 rounded-full transition-all duration-500 ${d === quoteIdx ? "w-7 bg-rosedeep" : "w-2 bg-blush hover:bg-rose/50"}`}
                  />
                ))}
              </div>
              <button
                onClick={nextQuote}
                aria-label="Frase seguinte"
                data-testid="quote-next"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-rose/40 text-rosedeep transition-all duration-300 hover:bg-rosedeep hover:text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-7 font-script text-2xl text-rose-gradient">Bloom Beauty Concept Hair</p>
          </div>
        </Reveal>
      </section>
    </Page>
  );
};

export default Home;
